import { useState } from "react";
import { Loader2, Sparkles, Wand2 } from "lucide-react";
import { Card } from "@/ui";
import { cn } from "@/lib/cn";
import { aiAssist } from "@/data/services";
import { getRegistrySpec } from "@/editor/registrySpec";
import type { BuilderProposal } from "./useBuilder";

/**
 * Whole-page generation from a one-line brief (capability #12). Calls the
 * ai-agent `/generate-page` endpoint (which builds confidently instead of
 * asking a clarifying question) and applies the returned proposal to the
 * working tree. Shown on near-empty pages so a blank page becomes a starting
 * point in one step.
 */
const EXAMPLES = [
  "A high-converting landing page for a digital savings account with no fees",
  "A page for a new premium credit card aimed at frequent travellers",
  "A mortgage pre-approval landing page that builds trust and urgency",
];

export function GeneratePagePanel({
  pageId,
  onApply,
  disabled,
}: {
  pageId: string | undefined;
  onApply: (p: BuilderProposal) => void;
  disabled?: boolean;
}) {
  const [brief, setBrief] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const generate = async (text: string) => {
    const b = text.trim();
    if (!b || loading || disabled) return;
    setLoading(true);
    setError(null);
    setDone(false);
    try {
      const r = await aiAssist.generatePage({
        brief: b,
        page_id: pageId,
        registry_spec: getRegistrySpec(),
      });
      const proposal = r.proposal as (Omit<BuilderProposal, "key"> & { id?: string }) | undefined;
      if (r.ok && proposal && Array.isArray(proposal.ops)) {
        onApply({ ...proposal, key: proposal.id ?? "generated" } as BuilderProposal);
        setDone(true);
      } else {
        setError(r.error || "Couldn’t generate a page");
      }
    } catch {
      setError("Couldn’t reach the AI service");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-4">
      <div className="flex items-center gap-2">
        <span className="grid h-7 w-7 place-items-center rounded-full bg-accent-soft text-accent">
          <Wand2 className="h-4 w-4" strokeWidth={1.75} />
        </span>
        <div>
          <p className="text-[14px] font-semibold text-ink-strong">Generate a full page</p>
          <p className="text-[12px] text-muted">Describe it once — Nara builds the whole layout.</p>
        </div>
      </div>

      <textarea
        value={brief}
        onChange={(e) => setBrief(e.target.value)}
        rows={2}
        placeholder="e.g. A landing page for a no-fee digital savings account"
        className="mt-3 w-full resize-none rounded-btn border border-hairline bg-paper px-3 py-2 text-[13px] text-ink outline-none focus:border-accent/50 dark:bg-white/5"
      />

      <div className="mt-2 flex flex-wrap gap-1.5">
        {EXAMPLES.map((ex) => (
          <button
            key={ex}
            type="button"
            onClick={() => setBrief(ex)}
            className="rounded-full bg-paper-2 px-2 py-0.5 text-[11px] text-muted transition-colors hover:text-ink dark:bg-white/5"
          >
            {ex.length > 38 ? ex.slice(0, 38) + "…" : ex}
          </button>
        ))}
      </div>

      {error && <p className="mt-2 text-[12px] text-accent">{error}</p>}
      {done && !error && (
        <p className="mt-2 text-[12px] text-muted">
          Page built — review the blocks, tweak anything, then publish.
        </p>
      )}

      <button
        type="button"
        onClick={() => generate(brief)}
        disabled={!brief.trim() || loading || disabled}
        className={cn(
          "mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-btn bg-accent px-3 py-2 text-[13px] font-medium text-white transition-opacity",
          (!brief.trim() || loading || disabled) && "opacity-50",
        )}
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
            Building your page…
          </>
        ) : (
          <>
            <Sparkles className="h-4 w-4" strokeWidth={1.75} />
            Generate page
          </>
        )}
      </button>
    </Card>
  );
}
